/**
 * HUD + overlay controller (FS-12 slice). Every critical value is shown as text (never hue alone,
 * Design Bible §12). Strings are only rebuilt when the underlying integer changes (FS-02 T10).
 */
import type { ModeId } from '../game/modes/types';
import type { HullId } from '../player/hulls/types';
import { renderHowToPlay, type HowToPlayOptions } from './how-to-play';
import type { RevealHudId } from '../core/event-map';

export interface ResultsData {
  modeId: ModeId;
  modeName: string;
  hullName: string;
  /** Null when the mode has no score (Zen, FS-09 R24) — the row is omitted, never shown as 0. */
  score: number | null;
  peakResonance: number;
  overdriveTime: number;
  hazardsCleared: number;
  staggerCount: number;
  cause: string;
  won: boolean;
  ranked: boolean;
  isBest: boolean;
  sectorName: string;
  sectorIndex: number | null;
  /**
   * FS-12 R36 / FS-01 R41 — offer How to Play **once**, at the end of a profile's first run, as a
   * third action of equal weight beside Retry. Never auto-opened, never in place of Retry.
   */
  offerHowToPlay: boolean;
}

export interface SectorCardData {
  index: number;
  name: string;
  genre: string;
  bpm: number;
  bossName: string;
  unlocked: boolean;
  cleared: boolean;
  best: number;
  primary: number;
  secondary: number;
}

export interface ModeCardData {
  id: ModeId;
  displayName: string;
  blurb: string;
  unlocked: boolean;
  unlockHint: string;
  disabledReason: string | null;
}

export interface HullCardData {
  id: HullId;
  displayName: string;
  identity: string;
  unlocked: boolean;
  unlockHint: string;
}

/** Where How to Play was opened from, so it can return there (FS-12 R34, R24). */
export type HowToPlayOpener = 'title' | 'paused' | 'results';

export interface HudHandlers {
  onRetry(): void;
  onMenu(): void;
  onModeSelect(): void;
  onHowToPlay(from: HowToPlayOpener): void;
  /** FS-01 R14 — onboarding is re-runnable on demand. */
  onReplayIntro(): void;
}

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`missing #${id}`);
  return e as T;
}

const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;

/** FS-07 §5.5 PICKUP_NAME_DWELL. */
const PICKUP_NAME_DWELL_MS = 2500;

export class Hud {
  private hud = el('hud');
  private overlay = el('overlay');
  private card = el('overlay-card');
  private scoreEl = el('hud-score');
  private multEl = el('hud-mult');
  private tierEl = el('hud-tier');
  private resFill = el('hud-resonance-fill');
  private odTag = el('hud-overdrive');
  private powerupEl = el('hud-powerup-name');
  private touchPower = document.getElementById('btn-touch-power') as HTMLButtonElement | null;
  private nameTimer: number | null = null;
  private pips: HTMLElement[] = Array.from(document.querySelectorAll('#hud-beat .pip'));
  private scoreBlock = el('hud-score-block');
  private multBlock = el('hud-mult-block');
  private resonanceBlock = el('hud-resonance');
  private powerupBlock = el('hud-powerup');
  private controlGlyph = el('hud-control-glyph');
  private skipBtn = el('btn-skip-onboarding');
  private skipBound = false;

  private lastScore = -1;
  private lastMult = -1;
  private lastTier = -1;
  private lastPowerup = ' ';
  private lastBossPhase = ' ';
  private bossEl = el('hud-boss');
  private bossNameEl = el('hud-boss-name');
  private bossPhaseEl = el('hud-boss-phase');
  private bossFill = el('hud-boss-fill');
  private handlers: HudHandlers | null = null;

  private htpDispose: (() => void) | null = null;

  setHandlers(h: HudHandlers): void {
    this.handlers = h;
  }

  /**
   * Every screen swap resets the card's layout modifiers and tears down How to Play's Escape
   * handler. Leaving that listener attached would make Escape close a screen the player is no
   * longer on.
   */
  private prepCard(wide: boolean): void {
    this.htpDispose?.();
    this.htpDispose = null;
    this.card.classList.toggle('wide', wide);
    this.card.classList.remove('scroll');
  }

  /**
   * FS-12 R34 — reachable from title, pause and results, returning to whichever opened it. It is
   * never reachable from `playing` without first entering `paused`, which falls out of the state
   * machine: an overlay only exists in a non-playing state.
   */
  showHowToPlay(opts: HowToPlayOptions, onBack: () => void): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(true);
    this.card.classList.add('scroll');
    this.htpDispose = renderHowToPlay(this.card, opts, onBack);
  }

  /**
   * `showReplayIntro` gates FS-01 R14's "re-runnable on demand". There is no settings screen in
   * this build, so it lives on the title card — and only once onboarding has been completed, so a
   * first-time player is never offered a replay of something they have not seen.
   */
  showMenuScreen(onStart: () => void, showReplayIntro = false): void {
    this.overlay.classList.remove('hidden');
    this.hud.classList.add('hidden');
    this.prepCard(false);
    this.card.innerHTML = MENU_HTML;
    el('btn-start').addEventListener('click', onStart);
    el('btn-htp-title').addEventListener('click', () => this.handlers?.onHowToPlay('title'));
    const replay = el('btn-replay-intro');
    replay.classList.toggle('hidden', !showReplayIntro);
    replay.addEventListener('click', () => this.handlers?.onReplayIntro());
  }

  /** The six-mode front door (FS-09 §4.1). */
  showModeSelect(modes: ModeCardData[], onChoose: (id: ModeId) => void, onBack: () => void): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(true);
    const cards = modes
      .map((m) => {
        const locked = !m.unlocked;
        const status = m.disabledReason ? m.disabledReason : locked ? 'LOCKED' : 'AVAILABLE';
        return `
        <button class="sector-card ${locked || m.disabledReason ? 'locked' : ''}" data-id="${m.id}"
          ${locked || m.disabledReason ? 'disabled' : ''} style="--sc-a:var(--cyan);--sc-b:var(--magenta)">
          <span class="sc-name">${escapeHtml(m.displayName)}</span>
          <span class="sc-meta">${escapeHtml(m.blurb)}</span>
          <span class="sc-status ${locked ? '' : 'cleared'}">${escapeHtml(locked ? m.unlockHint : status)}</span>
        </button>`;
      })
      .join('');
    this.card.innerHTML = `
      <h1 class="title small">SELECT MODE</h1>
      <p class="subtitle">R E S O N A N C E</p>
      <div class="sector-grid">${cards}</div>
      <div class="hints"><p><a id="btn-back" class="link">back</a></p></div>
    `;
    this.card.querySelectorAll<HTMLButtonElement>('.sector-card:not(.locked)').forEach((b) => {
      b.addEventListener('click', () => onChoose(b.dataset.id as ModeId));
    });
    el('btn-back').addEventListener('click', onBack);
  }

  /** Sector select — `Sector Run`'s starting point. Locked sectors state their unlock condition. */
  showSectorSelect(sectors: SectorCardData[], onChoose: (index: number) => void, onBack: () => void): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(true);
    const cards = sectors
      .map((s) => {
        const status = s.cleared ? 'CLEARED' : s.unlocked ? 'AVAILABLE' : 'LOCKED';
        const best = s.best > 0 ? `<span class="sc-best">BEST ${s.best.toLocaleString()}</span>` : '';
        return `
        <button class="sector-card ${s.unlocked ? '' : 'locked'}" data-index="${s.index}" ${s.unlocked ? '' : 'disabled'}
          style="--sc-a:${hex(s.primary)};--sc-b:${hex(s.secondary)}">
          <span class="sc-num">${s.index}</span>
          <span class="sc-name">${escapeHtml(s.name)}</span>
          <span class="sc-meta">${escapeHtml(s.genre)} · ${s.bpm} BPM</span>
          <span class="sc-boss">${escapeHtml(s.bossName)}</span>
          <span class="sc-status ${status.toLowerCase()}">${s.unlocked ? status : 'CLEAR PREVIOUS SECTOR'}</span>
          ${best}
        </button>`;
      })
      .join('');
    this.card.innerHTML = `
      <h1 class="title small">SELECT STARTING SECTOR</h1>
      <p class="subtitle">S E C T O R &nbsp; R U N</p>
      <div class="sector-grid">${cards}</div>
      <div class="hints"><p><a id="btn-back" class="link">back</a></p></div>
    `;
    this.card.querySelectorAll<HTMLButtonElement>('.sector-card:not(.locked)').forEach((b) => {
      b.addEventListener('click', () => onChoose(Number(b.dataset.index)));
    });
    el('btn-back').addEventListener('click', onBack);
  }

  /** Hull select (FS-10 §4.1) — every mode passes through here before the run begins. */
  showHullSelect(hulls: HullCardData[], onChoose: (id: HullId) => void, onBack: () => void): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(true);
    const cards = hulls
      .map((h) => {
        const locked = !h.unlocked;
        return `
        <button class="sector-card ${locked ? 'locked' : ''}" data-id="${h.id}" ${locked ? 'disabled' : ''}
          style="--sc-a:var(--cyan);--sc-b:var(--magenta)">
          <span class="sc-name">${escapeHtml(h.displayName)}</span>
          <span class="sc-meta">${escapeHtml(h.identity)}</span>
          <span class="sc-status ${locked ? '' : 'cleared'}">${escapeHtml(locked ? h.unlockHint : 'AVAILABLE')}</span>
        </button>`;
      })
      .join('');
    this.card.innerHTML = `
      <h1 class="title small">SELECT HULL</h1>
      <p class="subtitle">S H I P</p>
      <div class="sector-grid">${cards}</div>
      <div class="hints"><p><a id="btn-back" class="link">back</a></p></div>
    `;
    this.card.querySelectorAll<HTMLButtonElement>('.sector-card:not(.locked)').forEach((b) => {
      b.addEventListener('click', () => onChoose(b.dataset.id as HullId));
    });
    el('btn-back').addEventListener('click', onBack);
  }

  /** Custom Track's file picker (FS-09 R26/R27) — nothing here ever leaves the device. */
  showCustomUpload(onFile: (file: File) => void, onBack: () => void, errorMessage?: string): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(false);
    this.card.innerHTML = `
      <h1 class="title small">CUSTOM TRACK</h1>
      <p class="subtitle">Y O U R &nbsp; S O N G</p>
      <p class="pitch">Pick an audio file. It never leaves this device — the chart is generated live, on-device, and the run is unranked.</p>
      ${errorMessage ? `<p class="best-tag" style="color:var(--sun)">${escapeHtml(errorMessage)}</p>` : ''}
      <input type="file" id="custom-file-input" accept="audio/*" class="hidden" />
      <button class="btn" id="btn-pick-file">CHOOSE FILE</button>
      <div class="hints"><p><a id="btn-back" class="link">back</a></p></div>
    `;
    const input = el<HTMLInputElement>('custom-file-input');
    input.addEventListener('change', () => {
      const f = input.files?.[0];
      if (f) onFile(f);
    });
    el('btn-pick-file').addEventListener('click', () => input.click());
    el('btn-back').addEventListener('click', onBack);
  }

  showAnalyzing(): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(false);
    this.card.innerHTML = `<h1 class="title small">ANALYSING…</h1><p class="pitch">Estimating tempo on-device.</p>`;
  }

  showMessage(title: string, body: string, onBack: () => void): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(false);
    this.card.innerHTML = `
      <h1 class="title small">${escapeHtml(title)}</h1>
      <p class="pitch">${escapeHtml(body)}</p>
      <div class="hints"><p><a id="btn-back" class="link">back</a></p></div>
    `;
    el('btn-back').addEventListener('click', onBack);
  }

  showPaused(onResume: () => void, onQuit: () => void): void {
    this.overlay.classList.remove('hidden');
    this.prepCard(false);
    this.card.innerHTML = `
      <h1 class="title small">PAUSED</h1>
      <button class="btn" id="btn-resume">RESUME</button>
      <div class="hints"><p><a id="btn-htp-paused" class="link">how to play</a> · <a id="btn-quit" class="link">quit to menu</a></p></div>
    `;
    el('btn-resume').addEventListener('click', onResume);
    el('btn-htp-paused').addEventListener('click', () => this.handlers?.onHowToPlay('paused'));
    el('btn-quit').addEventListener('click', onQuit);
  }

  showHud(): void {
    this.htpDispose?.();
    this.htpDispose = null;
    this.overlay.classList.add('hidden');
    this.hud.classList.remove('hidden');
    this.lastScore = this.lastMult = this.lastTier = -1;
  }

  // ── Onboarding surfaces (FS-01 R10, R13, R15) ───────────────────────────

  /**
   * FS-01 R13 — HUD elements appear at the moment the mechanic they describe first becomes
   * relevant, not before. `progressive(true)` hides the four revealable blocks; each `reveal` call
   * brings one back. Outside onboarding everything is visible from the first frame, because the
   * reveal state belongs to the segment and never to the profile.
   */
  setProgressive(on: boolean): void {
    this.hud.classList.toggle('progressive', on);
    if (!on) {
      for (const el of this.revealables()) el.classList.remove('revealed');
    }
  }

  reveal(element: RevealHudId): void {
    const map: Record<RevealHudId, HTMLElement> = {
      score: this.scoreBlock,
      multiplier: this.multBlock,
      resonance: this.resonanceBlock,
      'powerup-slot': this.powerupBlock,
    };
    map[element]?.classList.add('revealed');
  }

  private revealables(): HTMLElement[] {
    return [this.scoreBlock, this.multBlock, this.resonanceBlock, this.powerupBlock];
  }

  /**
   * FS-01 R10 — exactly one control glyph, once, as a non-blocking overlay on the hull, gone
   * permanently on the first steering input. It is a **glyph, not text**: AC-16 forbids any text
   * node but the score over the playfield, and a glyph that spelled out "press ←/→" would be the
   * input-prompt banner R09 rules out.
   */
  showControlGlyph(touch: boolean): void {
    this.controlGlyph.classList.remove('hidden');
    this.controlGlyph.textContent = touch ? '⇠ ⇢' : '◄ ►';
  }

  hideControlGlyph(): void {
    this.controlGlyph.classList.add('hidden');
  }

  /** FS-01 R15 — not presented before `ONBOARD_SKIP_OFFER_T`, and never blocking. */
  setSkipVisible(visible: boolean, onSkip?: () => void): void {
    this.skipBtn.classList.toggle('hidden', !visible);
    if (visible && onSkip && !this.skipBound) {
      this.skipBound = true;
      this.skipBtn.addEventListener('click', onSkip);
    }
  }

  setScoreVisible(visible: boolean): void {
    this.scoreBlock.classList.toggle('hidden', !visible);
    this.multBlock.classList.toggle('hidden', !visible);
  }

  setScore(n: number): void {
    const v = Math.floor(n);
    if (v === this.lastScore) return;
    this.lastScore = v;
    this.scoreEl.textContent = v.toLocaleString();
  }

  setMultiplier(m: number): void {
    if (m === this.lastMult) return;
    this.lastMult = m;
    this.multEl.textContent = `${m}×`;
  }

  setTier(t: number): void {
    if (t === this.lastTier) return;
    this.lastTier = t;
    this.tierEl.textContent = `${t}`;
  }

  setResonance(value: number, overdrive: boolean): void {
    this.resFill.style.width = `${Math.max(0, Math.min(100, value))}%`;
    this.resFill.classList.toggle('overdrive', overdrive);
    this.odTag.classList.toggle('hidden', !overdrive);
  }

  setPowerup(name: string | null): void {
    const v = name ?? '—';
    if (v === this.lastPowerup) return;
    this.lastPowerup = v;
    this.powerupEl.textContent = v;
    // The on-screen USE control reflects the slot, so a player is never invited to press a
    // button that would be refused (FS-07 F8).
    if (this.touchPower) this.touchPower.disabled = name === null;
  }

  /**
   * FS-07 R37 — on collection, name the power-up *and* its activation input, at the slot rather
   * than the screen centre, non-blocking, for `PICKUP_NAME_DWELL`. "Harmonic Shield — SPACE"
   * answers the two questions a new player has, in the order they have them. `input` is named for
   * the device in their hands (R37, FS-12 R40).
   */
  namePickup(name: string, input: string, firstEver: boolean): void {
    const slot = this.powerupEl.parentElement;
    if (!slot) return;
    slot.classList.add('named');
    if (firstEver) slot.classList.add('first-ever');
    this.powerupEl.textContent = `${name} — ${input}`;
    this.lastPowerup = this.powerupEl.textContent;
    if (this.nameTimer !== null) clearTimeout(this.nameTimer);
    this.nameTimer = window.setTimeout(() => {
      slot.classList.remove('named', 'first-ever');
      // Fall back to the plain stored name; setPowerup's dedupe is primed by lastPowerup above.
      this.lastPowerup = '';
      this.setPowerup(name);
      this.nameTimer = null;
    }, PICKUP_NAME_DWELL_MS);
  }

  /** Light the beat pips: `beatInBar` 0..3, `on` true on the beat instant. */
  beat(beatInBar: number): void {
    for (let i = 0; i < this.pips.length; i++) {
      const pip = this.pips[i]!;
      pip.classList.toggle('on', i === beatInBar);
      pip.classList.toggle('downbeat', beatInBar === 0);
    }
  }

  /**
   * There is no health bar (FS-08 R25) — a boss is beaten by surviving, so the readout is the phase
   * count and the bars remaining. The fill shows progress through the phase list, not "damage".
   * `incoming` is the telegraphed next phase (R29), announced as text: a non-motion, non-hue channel.
   */
  showBoss(
    name: string,
    phaseName: string,
    phaseIndex: number,
    phaseCount: number,
    barsRemaining: number,
    incoming: string | null,
    jam: boolean,
  ): void {
    this.bossEl.classList.remove('hidden');
    this.bossNameEl.textContent = name;
    const line = incoming
      ? `NEXT: ${incoming}`
      : `PHASE ${Math.min(phaseIndex + 1, phaseCount)}/${phaseCount} · ${phaseName} · ${barsRemaining} BARS`;
    if (line !== this.lastBossPhase) {
      this.lastBossPhase = line;
      this.bossPhaseEl.textContent = line;
    }
    this.bossFill.style.width = `${Math.round((phaseIndex / phaseCount) * 100)}%`;
    // Cosmetic only — values are never altered (The Jammer's fairness argument).
    this.hud.classList.toggle('jammed', jam);
  }

  hideBoss(): void {
    this.bossEl.classList.add('hidden');
    this.hud.classList.remove('jammed');
    this.lastBossPhase = ' ';
  }

  showResults(d: ResultsData): void {
    this.hud.classList.add('hidden');
    this.overlay.classList.remove('hidden');
    this.prepCard(false);
    const sectorBit = d.sectorIndex != null ? `Sector ${d.sectorIndex} · ${d.sectorName} · ` : '';
    const sub = `${d.modeName} · ${d.hullName} · ${sectorBit}${d.cause}`;
    const scoreRow =
      d.score != null
        ? `<div><div class="k">SCORE</div><div class="v">${Math.floor(d.score).toLocaleString()}</div></div>`
        : `<div><div class="k">STAGGERS</div><div class="v">${d.staggerCount}</div></div>`;
    const rankTag = !d.ranked ? '<p class="best-tag" style="color:var(--cyan)">UNRANKED</p>' : '';
    this.card.innerHTML = `
      <h1 class="title ${d.won ? '' : 'small'}">${d.won ? 'RUN COMPLETE' : 'RUN OVER'}</h1>
      <p class="subtitle tight">${escapeHtml(sub)}</p>
      ${d.isBest ? '<p class="best-tag">NEW BEST</p>' : ''}
      ${rankTag}
      <div class="result-stats">
        ${scoreRow}
        <div><div class="k">PEAK RESONANCE</div><div class="v">${Math.round(d.peakResonance)}</div></div>
        <div><div class="k">OVERDRIVE TIME</div><div class="v">${d.overdriveTime.toFixed(1)}s</div></div>
        <div><div class="k">HAZARDS CLEARED</div><div class="v">${d.hazardsCleared}</div></div>
      </div>
      <div class="result-actions">
        <button class="btn" id="btn-retry">RETRY</button>
        ${d.offerHowToPlay ? '<button class="btn ghost" id="btn-htp-results">HOW TO PLAY</button>' : ''}
      </div>
      <div class="hints"><p><a id="btn-modes" class="link">mode select</a> · <a id="btn-menu" class="link">menu</a></p></div>
    `;
    el('btn-retry').addEventListener('click', () => this.handlers?.onRetry());
    if (d.offerHowToPlay) {
      el('btn-htp-results').addEventListener('click', () => this.handlers?.onHowToPlay('results'));
    }
    el('btn-modes').addEventListener('click', () => this.handlers?.onModeSelect());
    el('btn-menu').addEventListener('click', () => this.handlers?.onMenu());
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

const MENU_HTML = `
  <h1 class="title">SPACE&nbsp;WAVES</h1>
  <p class="subtitle">R E S O N A N C E</p>
  <p class="pitch">You fly a ship down a tunnel made of music.<br />The waveform is the level. Dodge it, graze it, ride the beat.</p>
  <button class="btn" id="btn-start">START</button>
  <div class="hints">
    <p><b>Move</b> — Arrow keys / WASD / drag</p>
    <p><b>Graze</b> hazards to build Resonance → Overdrive</p>
    <p><b>Space</b> — use stored power-up · <b>P</b> — pause</p>
    <p><a id="btn-htp-title" class="link">how to play</a><span id="btn-replay-intro" class="hidden"> · <a class="link">replay intro</a></span></p>
  </div>
  <p class="audio-note">Audio starts on first input. Fully playable muted — watch the beat pips.</p>
`;
